import {
  Box,
  Grid,
  Stack,
  Typography,
  Skeleton
} from '@mui/material';
import MainCard from 'components/MainCard';
import ProviderChip from 'components/ProviderChip';
import { useGetUserBodySummaryQuery } from 'features/api/apiSlice';
import PatientMetricCard from 'pages/patients/tabs/PatientMetricCard';
import {
  Scale,
  Percent,
  Calculator,
  Dumbbell,
  Heart,
  Activity,
  Ruler,
  Thermometer
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

interface BodyTabProps {
  userId: string;
}

interface BodySummary {
  source?: {
    provider?: string;
    source?: string | null;
    device?: string | null;
    device_name?: string | null;
  } | null;
  slow_changing?: {
    weight_kg?: number | null;
    height_cm?: number | null;
    body_fat_percent?: number | null;
    muscle_mass_kg?: number | null;
    bmi?: number | null;
  };
  averaged?: {
    resting_heart_rate_bpm?: number | null;
    avg_hrv_sdnn_ms?: number | null;
    avg_hrv_rmssd_ms?: number | null;
  };
  latest?: {
    body_temperature_celsius?: number | null;
    blood_pressure?: { systolic?: number; diastolic?: number } | null;
  };
}

// ============================================================================
// Period Toggle Component
// ============================================================================

interface PeriodToggleProps {
  value: 1 | 7;
  onChange: (value: 1 | 7) => void;
}

function PeriodToggle({ value, onChange }: PeriodToggleProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: 'grey.100', borderRadius: 1, alignItems: 'center' }}>
      {([1, 7] as const).map((period) => (
        <Box
          key={period}
          component="button"
          type="button"
          aria-pressed={value === period}
          onClick={() => onChange(period)}
          sx={{
            border: 'none',
            bgcolor: value === period ? 'grey.300' : 'transparent',
            color: value === period ? 'text.primary' : 'text.secondary',
            px: 1,
            py: 0.4,
            borderRadius: 0.75,
            fontSize: '0.7rem',
            fontWeight: 600,
            lineHeight: 1.2,
            cursor: 'pointer',
            minWidth: '28px'
          }}
        >
          {period === 1 ? t('Today', 'Today') : t('7 Days', '7 Days')}
        </Box>
      ))}
    </Box>
  );
}

// ============================================================================
// Loading Skeleton
// ============================================================================

function MetricCardSkeleton() {
  return (
    <Box
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%'
      }}
    >
      <Skeleton variant="rounded" width={36} height={36} sx={{ mb: 1.5 }} />
      <Skeleton variant="text" width="65%" height={38} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="45%" height={20} />
    </Box>
  );
}

function BodySectionSkeleton() {
  return (
    <Stack spacing={3}>
      {/* Body Composition Skeleton */}
      <Box>
        <Typography variant="caption" sx={{ mb: 2, display: 'block', color: 'text.secondary' }}>
          <Skeleton width="100px" />
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={6} sm={4} md={2.4} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Vitals Skeleton */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Skeleton variant="text" width={48} height={20} />
          <Skeleton variant="rounded" width={100} height={30} />
        </Stack>
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Recent Readings Skeleton */}
      <Box>
        <Typography variant="caption" sx={{ mb: 2, display: 'block', color: 'text.secondary' }}>
          <Skeleton width="100px" />
        </Typography>
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function BodyTab({ userId }: BodyTabProps) {
  const { t } = useTranslation();
  const [averagePeriod, setAveragePeriod] = useState<1 | 7>(7);
  const { currentData: data, isLoading, isFetching } = useGetUserBodySummaryQuery({
    userId,
    average_period: averagePeriod
  });
  const bodySummary = data as BodySummary | undefined;

  const slowChangingData = bodySummary?.slow_changing;

  const averagedData = bodySummary?.averaged;

  const latestData = bodySummary?.latest;

  const getBmiCategory = (bmi: number | null | undefined) => {
    if (bmi === null || bmi === undefined) {
      return { label: '', color: 'text.secondary' };
    }
    if (bmi < 18.5) return { label: t('Underweight', 'Underweight'), color: 'info.main' };
    if (bmi < 25) return { label: t('Normal', 'Normal'), color: 'success.main' };
    if (bmi < 30) return { label: t('Overweight', 'Overweight'), color: 'warning.main' };
    return { label: t('Obese', 'Obese'), color: 'error.main' };
  };

  const bmiCategory = getBmiCategory(slowChangingData?.bmi);

  const formatWeight = (value: number | null): string => value ? `${value.toFixed(1)} kg` : '—';
  const formatHeight = (value: number | null): string => value ? `${value.toFixed(0)} cm` : '—';
  const formatPercent = (value: number | null): string => value ? `${value.toFixed(1)}%` : '—';
  const formatBmi = (value: number | null): string => {
    if (!value) return '—';
    if (value < 18.5) return value.toFixed(1);
    if (value < 25) return value.toFixed(1);
    if (value < 30) return value.toFixed(1);
    return value.toFixed(1);
  };
  const formatTemperature = (value: number | null): string => value ? `${value.toFixed(1)}°C` : '—';
  const formatHeartRate = (value: number | null | undefined): string => value ? `${Math.round(value)} bpm` : '—';
  const formatHrv = (value: number | null): string => value ? `${Math.round(value)} ms` : '—';

  const formatBloodPressure = (bp: { systolic?: number; diastolic?: number } | null | undefined): string => {
    if (!bp || !bp.systolic || !bp.diastolic) return '—';
    return `${bp.systolic}/${bp.diastolic} mmHg`;
  };

  return (
    <MainCard
      title={
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1">
            {t('Body Metrics', 'Body Metrics')}
          </Typography>
          {bodySummary?.source?.provider && (
            <ProviderChip provider={bodySummary.source.provider} />
          )}
        </Stack>
      }
    >
      <Stack spacing={3}>
        {isLoading ? (
          <BodySectionSkeleton />
        ) : (
          <>
            {/* Body Composition Section */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary'
                  }}
                >
                  {t('Body Composition', 'Body Composition')}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2.4}>
                  <PatientMetricCard
                    icon={Scale}
                    accent="#60a5fa"
                    value={formatWeight(slowChangingData?.weight_kg ?? null)}
                    label={t('Weight', 'Weight')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <PatientMetricCard
                    icon={Ruler}
                    accent="#22d3ee"
                    value={formatHeight(slowChangingData?.height_cm ?? null)}
                    label={t('Height', 'Height')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <PatientMetricCard
                    icon={Percent}
                    accent="#fb923c"
                    value={formatPercent(slowChangingData?.body_fat_percent ?? null)}
                    label={t('Body Fat', 'Body Fat')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <PatientMetricCard
                    icon={Dumbbell}
                    accent="#4ade80"
                    value={formatWeight(slowChangingData?.muscle_mass_kg ?? null)}
                    label={t('Muscle Mass', 'Muscle Mass')}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                  <PatientMetricCard
                    icon={Calculator}
                    accent="#c084fc"
                    value={formatBmi(slowChangingData?.bmi ?? null)}
                    label={t('BMI', 'BMI')}
                    sublabel={bmiCategory.label || undefined}
                    sublabelColor={bmiCategory.color}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Vitals Section */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary'
                  }}
                >
                  {t('Vitals', 'Vitals')} {averagePeriod === 1 ? `(${t('Today', 'Today')})` : `(${t('7 Days', '7 Days')})`}
                </Typography>
                <PeriodToggle value={averagePeriod} onChange={setAveragePeriod} />
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <PatientMetricCard
                    icon={Heart}
                    accent="#fb7185"
                    value={isFetching ? '—' : formatHeartRate(averagedData?.resting_heart_rate_bpm)}
                    label={t('Resting HR', 'Resting HR (bpm)')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PatientMetricCard
                    icon={Activity}
                    accent="#818cf8"
                    value={isFetching ? '—' : formatHrv(
                      averagedData?.avg_hrv_sdnn_ms ?? averagedData?.avg_hrv_rmssd_ms ?? null
                    )}
                    label={
                      averagedData?.avg_hrv_sdnn_ms !== null &&
                      averagedData?.avg_hrv_sdnn_ms !== undefined
                        ? t('HRV SDNN', 'HRV SDNN (ms)')
                        : t('HRV RMSSD', 'HRV RMSSD (ms)')
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Recent Readings Section */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary'
                  }}
                >
                  {t('Recent Readings', 'Recent Readings')}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <PatientMetricCard
                    icon={Thermometer}
                    accent="#fbbf24"
                    value={formatTemperature(latestData?.body_temperature_celsius ?? null)}
                    label={t('Body Temperature', 'Body Temperature')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PatientMetricCard
                    icon={Activity}
                    accent="#f87171"
                    value={formatBloodPressure(latestData?.blood_pressure)}
                    label={t('Blood Pressure', 'Blood Pressure')}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Stack>
    </MainCard>
  );
}
